/**
 * collection-load-more.js
 *
 * Intercepts the theme's infinite-scroll sentinel and replaces it with a
 * manual "Load More Products" button.  When clicked the button briefly moves
 * the sentinel into the viewport so the existing PaginatedList
 * IntersectionObserver fires and appends the next page — no page reload.
 *
 * Also re-injects the button after filter updates because the section
 * renderer replaces the inner DOM on every filter change.
 */
(function () {
  const FILTER_EVENT = 'filter:update';

  // Keep the sentinel permanently off-screen so the observer never auto-fires
  function hideSentinel(sentinel) {
    sentinel.style.cssText =
      'position:fixed;top:-9999px;left:0;width:1px;height:1px;';
  }

  // Returns true if there are still more pages to load
  function hasMorePages(grid) {
    if (!grid) return false;
    const lastPage = Number(grid.dataset.lastPage || 1);
    const cards = grid.querySelectorAll('[ref="cards[]"]');
    const lastCard = cards[cards.length - 1];
    const lastCardPage = Number(lastCard?.dataset.page || 1);
    return lastCardPage < lastPage;
  }

  function inject(resultsEl) {
    const sentinel = resultsEl.querySelector('[ref="viewMoreNext"]');
    if (!sentinel) return; // infinite scroll not enabled

    const grid = resultsEl.querySelector('[ref="grid"]');

    // Remove any previously injected button (e.g. after filter re-render)
    resultsEl.querySelector('.badais-load-more-btn')?.remove();

    hideSentinel(sentinel);

    const btn = document.createElement('button');
    btn.className = 'badais-load-more-btn';
    btn.textContent = 'Load More Products';
    sentinel.parentNode.insertBefore(btn, sentinel);

    if (!hasMorePages(grid)) btn.style.display = 'none';

    btn.addEventListener('click', function () {
      if (btn.disabled) return;
      btn.disabled = true;
      btn.textContent = 'Loading…';

      const countBefore = grid
        ? grid.querySelectorAll('[ref="cards[]"]').length
        : 0;

      // Prevent the theme's paginated-list from updating the URL (?page=2 etc.)
      // so a reload always returns the user to page 1.
      const originalPushState = history.pushState.bind(history);
      history.pushState = function () {};

      // Move sentinel into the viewport — triggers IntersectionObserver
      sentinel.style.cssText =
        'position:fixed;bottom:0;left:0;width:1px;height:1px;z-index:-1;';

      function restore() {
        hideSentinel(sentinel);
        history.pushState = originalPushState;
        btn.disabled = false;
        btn.textContent = 'Load More Products';
        if (!hasMorePages(grid)) btn.style.display = 'none';
      }

      const mo = new MutationObserver(function () {
        if (!grid) return;
        const countAfter = grid.querySelectorAll('[ref="cards[]"]').length;
        if (countAfter > countBefore) {
          mo.disconnect();
          restore();
        }
      });

      if (grid) mo.observe(grid, { childList: true });

      // Fallback: restore after 8 s
      setTimeout(function () {
        mo.disconnect();
        restore();
      }, 8000);
    });
  }

  function setup(resultsEl) {
    inject(resultsEl);

    // After a filter update the section renderer replaces the inner DOM,
    // so we wait for data-last-page to change on the grid, then re-inject.
    document.addEventListener(FILTER_EVENT, function () {
      const grid = resultsEl.querySelector('[ref="grid"]');
      const currentLastPage = grid?.dataset.lastPage;

      const mo = new MutationObserver(function () {
        const newLastPage = resultsEl.querySelector('[ref="grid"]')?.dataset.lastPage;
        // Fire once the grid DOM has actually been replaced / updated
        if (newLastPage !== undefined) {
          mo.disconnect();
          inject(resultsEl);
        }
      });

      mo.observe(resultsEl, { childList: true, subtree: true, attributes: true,
        attributeFilter: ['data-last-page'] });

      // Fallback disconnect after 5 s
      setTimeout(function () { mo.disconnect(); }, 5000);
    });
  }

  function waitForInit() {
    const resultsEl = document.querySelector('results-list');
    if (!resultsEl) return;

    if (resultsEl.hasAttribute('initialized')) {
      setup(resultsEl);
    } else {
      new MutationObserver(function (_, obs) {
        if (resultsEl.hasAttribute('initialized')) {
          obs.disconnect();
          setup(resultsEl);
        }
      }).observe(resultsEl, { attributes: true, attributeFilter: ['initialized'] });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForInit, { once: true });
  } else {
    waitForInit();
  }
})();
